import posthog from "posthog-js";
import {
  findCometScript,
  isCometPixelReady,
  markCometPixelUnavailable,
  readCometToken,
} from "src/utils/cometToken";

let posthogInitialized = false;
let posthogStartRequested = false;

// One ceiling for a pixel that never loads. This is not a retry loop.
const COMET_PIXEL_WAIT_MS = 3000;

function registerCometToken(posthogClient) {
  const token = readCometToken();
  if (!token || typeof posthogClient.register !== "function") return;
  posthogClient.register({ comet_token: token });
}

function attachCometToken(event) {
  if (!event) return event;
  const token = readCometToken();
  if (!token) return event;
  event.properties = { ...(event.properties || {}), comet_token: token };
  return event;
}

// The pixel is injected from index.html and may still be downloading when
// PostHog starts. Wait for that one load or error. If the token is already
// available, or the pixel is not on this environment, start immediately.
// index.html stores a cross-domain override before this runs, so a stored
// token is never an older one the pixel is about to replace.
function whenCometPixelSettled(onSettled) {
  if (readCometToken()) {
    onSettled();
    return;
  }

  const script = findCometScript();
  if (!script) {
    onSettled();
    return;
  }

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    script.removeEventListener("load", onLoad);
    script.removeEventListener("error", onError);
    onSettled();
  };
  const onLoad = () => {
    finish();
  };
  const onError = () => {
    markCometPixelUnavailable();
    finish();
  };

  script.addEventListener("load", onLoad, { once: true });
  script.addEventListener("error", onError, { once: true });
  const timeoutId = window.setTimeout(finish, COMET_PIXEL_WAIT_MS);

  if (readCometToken()) finish();
}

function startPostHog(apiKey, host) {
  if (posthogInitialized) return;

  try {
    posthog.init(apiKey, {
      api_host: host,
      debug: false,
      capture_pageview: false, // Disabled - pageview will be fired manually after inhouse_anonymous_id is ready
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: false,
        },
      },
      enable_heatmaps: true,
      autocapture: true,
      cross_subdomain_cookie: true,
      secure_cookie: true,
      before_send: attachCometToken,
      loaded: (posthogClient) => {
        registerCometToken(posthogClient);
        if (!isCometPixelReady()) {
          const script = findCometScript();
          if (script) {
            script.addEventListener("load", () => registerCometToken(posthogClient), { once: true });
          }
        }
      },
    });
    posthogInitialized = true;
  } catch (error) {
    console.error("PostHog initialization failed:", error);
  }
}

export const initPostHog = () => {
  if (typeof window === "undefined" || posthogStartRequested) {
    return;
  }

  const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://us.posthog.com";

  if (!apiKey) {
    console.warn("PostHog API key not found. PostHog tracking will be disabled.");
    return;
  }

  posthogStartRequested = true;
  whenCometPixelSettled(() => startPostHog(apiKey, host));
};

export const getPostHog = async () => {
  return new Promise((resolve) => {
    const checkPostHog = (counter = 0) => {
      if (counter === 30) {
        resolve(null);
        return;
      }
      if (posthogInitialized && posthog && posthog.capture) {
        resolve(posthog);
      } else {
        setTimeout(() => checkPostHog(counter + 1), 1000);
      }
    };
    checkPostHog();
  });
};

export const trackPostHogEvent = async (eventName, properties = {}) => {
  try {
    const posthogClient = await getPostHog();
    if (!posthogClient) {
      return;
    }

    posthogClient.capture(eventName, properties);
  } catch (error) {
    console.error("Unable to track PostHog event:", {
      eventName,
      properties,
      error,
    });
  }
};

export const identifyPostHogUser = async (userId, properties = {}) => {
  try {
    const posthogClient = await getPostHog();
    if (!posthogClient) {
      return;
    }

    posthogClient.identify(userId, properties);
  } catch (error) {
    console.error("Unable to identify PostHog user:", {
      userId,
      properties,
      error,
    });
  }
};

export const setPostHogPersonProperties = async (properties) => {
  try {
    const posthogClient = await getPostHog();
    if (!posthogClient) {
      return;
    }

    posthogClient.setPersonProperties(properties);
  } catch (error) {
    console.error("Unable to set PostHog person properties:", error);
  }
};

/** The distinct ID PostHog is sending as, and whether identify() set it. */
export const getPostHogIdentity = async () => {
  const posthogClient = await getPostHog();
  if (!posthogClient) {
    return null;
  }

  return {
    distinctId: posthogClient.get_distinct_id(),
    isIdentified: posthogClient.get_property("$user_state") === "identified",
  };
};

export const resetPostHogUser = async () => {
  try {
    const posthogClient = await getPostHog();
    if (!posthogClient) {
      return;
    }

    posthogClient.reset();
  } catch (error) {
    console.error("Unable to reset PostHog user:", error);
  }
};

export const capturePostHogPageView = async (pageName) => {
  try {
    const posthogClient = await getPostHog();
    if (!posthogClient) {
      return;
    }

    posthogClient.capture("$pageview", {
      $current_url: window.location.href,
      page_name: pageName,
    });
  } catch (error) {
    console.error("Unable to capture PostHog pageview:", {
      pageName,
      error,
    });
  }
};
